import { useCallback, useMemo, useState } from "react";
import TopMenu from "components/InventoryItems/TopMenu";
import useSubcategories, { useCategories } from "graphql/useSubcategories";
import useTaxes from "graphql/useTaxes";
import List from "components/common/ListWithMoreOptions";
import useItems from "graphql/useItems";
import Modal from "components/common/ModalFullScreen";
import useItemMutations from "graphql/useItemMutations";
import { usePaginatedData } from "helpers/hooks";
import { useOrderItemSummary } from "graphql/useOrderItemSummary";
import { useBusinessContext } from "BusinessApp";
import _ from "lodash";
import { Spin } from "antd";
import moment from "moment";
import { useMessaging } from "context/MessagingContext";
import { useTranslation } from "react-i18next";
import BottomPageSelectorWithPagination, {
  usePagination,
} from "../../components/common/BottomPageSelectorWithPagination";
import { dbValueToFloat } from "../../helpers/utils";
import ItemForm from "./FormItem";
import s from "./styles";
import { useDatesContext } from "../../context/DatesContext";

function InventoryItems() {
  const { t } = useTranslation();
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [filter, setFilter] = useState("all");

  const { sorting, setSorting, search, setSearch, pagination, setPagination } =
    usePagination("name");

  function changeSearch(v) {
    setSearch(v);
    setPagination({ ...pagination, page: 0 });
  }

  const { storeId, currencyFormat } = useBusinessContext();

  const { week } = useDatesContext();

  const { setInfoMessage, setConfirmMessage, setErrorMessage } = useMessaging();

  const { orderItemSummary, loading: loading1 } = useOrderItemSummary({
    storeId,
    start: week.start,
    end: week.end,
  });

  const orderItemSummaryMap = useMemo(() => {
    const result = {};
    orderItemSummary.forEach((summary) => {
      if (summary._id.itemId) result[summary._id.itemId?.toString()] = summary;
    });
    return result;
  }, [orderItemSummary]);

  const { items, loading: loading2 } = useItems();
  const { taxMap, loading: loading3 } = useTaxes();
  const { categoryMap, loading: loading4 } = useCategories();
  const { subcategoryMap, loading: loading5 } = useSubcategories();

  const data = useMemo(() => {
    let itemsListFiltered = items.filter((i) =>
      i.name.toLocaleLowerCase().includes(search.toLowerCase())
    );
    itemsListFiltered = itemsListFiltered.map((item) => {
      const subcategory = subcategoryMap[item.subcategoryId];
      let category = "";
      if (subcategory) {
        category = categoryMap[subcategory.categoryId]?.name;
      }
      return {
        ...item,
        category: `${category} / ${(subcategory && subcategory.name) || ""}`,
        tax: (item.tax1 && taxMap[item.tax1]?.name) || "",
        tax1: item.tax1,
        unitsSold: orderItemSummaryMap[item._id.toString()]?.quantity || 0,
      };
    });
    if (filter === "lowStock") {
      itemsListFiltered = itemsListFiltered.filter(
        (item) => item.stock < item.minStock
      );
    }
    return itemsListFiltered;
  }, [
    categoryMap,
    filter,
    items,
    orderItemSummaryMap,
    search,
    subcategoryMap,
    taxMap,
  ]);

  const dataToShow = usePaginatedData(
    data,
    pagination,
    sorting,
    (item) => [
      item.name,
      item.sku,
      item.category,
      `${currencyFormat(item.price)}/u`,
      `${item.tax || "IVA n"}`,
      moment(item.updateDatetime).format("lll"),
    ],
    {
      price: (a, b) => dbValueToFloat(a.price) - dbValueToFloat(b.price),
      tax: (a, b) =>
        dbValueToFloat(a.tax1 && taxMap[a.tax1]?.percentage) -
        dbValueToFloat(b.tax1 && taxMap[b.tax1]?.percentage),
    }
  );
  //
  const { deleteItem } = useItemMutations();

  const loading = loading1 || loading2 || loading3 || loading4 || loading5;

  const onUpdateSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.InventoryItems.editItem"),
      info1: t("containers.InventoryItems.findItem"),
    });
  }, []);

  const onAddSuccess = useCallback(() => {
    setInfoMessage({
      titleAlert: t("containers.InventoryItems.newItem"),
      info1: t("containers.InventoryItems.itemDB"),
    });
  }, []);

  const onDeleteRequest = useCallback((item) => {
    setConfirmMessage({
      titleAlert: t("containers.InventoryItems.delete"),
      confirm1: `${t("containers.InventoryItems.deleteItem")} ${item.name}?`,
      confirm2: t("containers.InventoryItems.actionCannotBeUndone"),
      onConfirm: () =>
        deleteItem(item)
          .then(() => {
            setAddItemModalOpen(false);
          })
          .catch((e) => setErrorMessage({ error: e })),
    });
  }, []);

  return (
    <s.PageContainer>
      <TopMenu
        selectedFilter={filter}
        changeSelectedFilter={(v) => setFilter(v)}
        changeSearchValue={(v) => changeSearch(v)}
        addItem={() => setAddItemModalOpen(true)}
        loading={loading}
      />
      {!loading && (
        <s.ListContainer>
          <List
            width="1300px"
            handleEdit={(item) => {
              const copy = _.cloneDeep(item.element);

              function cleanCopy(copy) {
                delete copy.__typename;
                delete copy?.modifierConfig?.__typename;
                delete copy?.mixAndMatch?.__typename;
                copy?.mixAndMatch?.steps?.forEach((item) => {
                  delete item.__typename;
                });
                copy?.mixAndMatch?.steps?.forEach((item) => {
                  item.items.forEach((item) => {
                    delete item.__typename;
                  });
                });
              }

              cleanCopy(copy);
              setItemToEdit(copy);
              setAddItemModalOpen(true);
            }}
            handleDeleteWorker={(item) => {
              onDeleteRequest(item.element);
            }}
            data={dataToShow}
            columns={[
              {
                title: t("containers.InventoryItems.item"),
                sortable: true,
                columnId: "name",
              },
              {
                title: t("containers.InventoryItems.sku"),
                sortable: true,
                columnId: "sku",
              },
              {
                title: t("containers.InventoryItems.category"),
                sortable: true,
                columnId: "category",
              },
              {
                title: t("containers.InventoryItems.price"),
                sortable: true,
                columnId: "price",
              },
              {
                title: t("containers.InventoryItems.tax"),
                sortable: true,
                columnId: "tax",
              },
              {
                title: t("containers.InventoryItems.lastUpdate"),
                sortable: true,
                columnId: "updateDatetime",
              },
              {
                title: "",
              },
            ]}
            sorting={sorting}
            changeSorting={(columnId, order) => setSorting({ columnId, order })}
            tableStyle={{
              gridTemplateColumns: "20fr 16fr 17fr 13fr 10fr 15fr 6fr",
            }}
          />
        </s.ListContainer>
      )}

      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Spin
            style={{ color: "#000" }}
            size="large"
            tip={t("containers.InventoryItems.loading")}
          />
        </div>
      )}
      {!loading && (
        <BottomPageSelectorWithPagination
          pagination={pagination}
          setPagination={setPagination}
          length={data.length}
        />
      )}

      {addItemModalOpen && (
        <Modal>
          <ItemForm
            storeId={storeId}
            closeModal={() => {
              setAddItemModalOpen(false);
              setItemToEdit(null);
            }}
            item={itemToEdit}
            onUpdateSuccess={onUpdateSuccess}
            onAddSuccess={onAddSuccess}
            onDeleteRequest={onDeleteRequest}
          />
        </Modal>
      )}
    </s.PageContainer>
  );
}

export default InventoryItems;
